import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./DeleteConfirmModal.module.css";
import { useLanguage } from "../../../../context/LanguageContext";

interface DeleteElementModalProps {
  isOpen: boolean;
  elementType: "primitive" | "transaction" | "step";
  elementName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function DeleteElementModal({
  isOpen,
  elementType,
  elementName,
  onConfirm,
  onCancel,
}: DeleteElementModalProps) {
  const { t } = useLanguage();

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onConfirm();
      }
      
      if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onConfirm, onCancel]);

  if (!isOpen) return null;

  const getTitleText = () => {
    switch (elementType) {
      case "primitive":
        return t("deleteElement");
      case "transaction":
        return t("deleteTransaction");
      case "step":
        return t("deleteStep");
      default:
        return t("deleteDefaultT");
    }
  };

  const getMessageText = () => {
    switch (elementType) {
      case "primitive":
        return t("deleteElementMessage");
      case "transaction":
        return t("deleteTransactionMessage");
      case "step":
        return t("deleteStepMessage");
      default:
        return t("deleteDefaultMessage");
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalIcon}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>

        <h3 className={styles.modalTitle}>{getTitleText()}</h3>

        {elementName && (
          <p className={styles.modalElementName}>
            <strong>{elementName}</strong>
          </p>
        )}

        <p className={styles.modalMessage}>{getMessageText()}</p>

        <div className={styles.modalButtons}>
          <button onClick={onCancel} className={styles.modalCancelBtn}>
            {t("cancel")}
          </button>

          <button onClick={onConfirm} className={styles.modalDeleteBtn}>
            <FontAwesomeIcon icon={faTrash} />
            {t("delete")}
          </button>
        </div>
      </div>
    </div>
  );
}